import { forwardRef, type MouseEvent as ReactMouseEvent } from "react";
import { playClick } from "../lib/sounds";

function isSameOriginPathNavigation(to: string) {
  try {
    return new URL(to, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

// The conditions under which handleMouseDown below navigates in-app. Exported
// so a side effect hung off a link can fire only when navigation follows: a
// secondary button or a modifier opens a new tab instead.
export function isInAppNavigation(event: {
  button: number;
  ctrlKey: boolean;
  metaKey: boolean;
}): boolean {
  return event.button === 0 && !event.ctrlKey && !event.metaKey;
}

interface Props extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  replace?: boolean;
}

export const Link = forwardRef<HTMLAnchorElement, Props>(
  ({ to, replace, target, onMouseDown, onClick, ...props }, ref) => {
    const handleMouseDown = (event: ReactMouseEvent<HTMLAnchorElement>) => {
      onMouseDown?.(event);
      if (event.defaultPrevented) return;
      if (target && target !== "_self") return;
      if (!isSameOriginPathNavigation(to)) return;
      if (!isInAppNavigation(event)) return;

      playClick();
      event.preventDefault();
      const method = replace ? "replaceState" : "pushState";
      history[method](history.state, "", to);
    };

    // Pointer navigation is handled by handleMouseDown. This handler prevents
    // the browser's default navigation for those, and also handles
    // keyboard-triggered clicks (Enter on a focused link) which bypass
    // mousedown entirely — detail === 0 identifies them.
    const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) return;
      if (target && target !== "_self") return;
      if (!isSameOriginPathNavigation(to)) return;
      if (event.button !== 0 || event.ctrlKey || event.metaKey) return;

      event.preventDefault();

      if (event.detail === 0) {
        playClick();
        const method = replace ? "replaceState" : "pushState";
        history[method](history.state, "", to);
      }
    };

    return (
      <a
        ref={ref}
        href={to}
        target={target}
        {...props}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      />
    );
  }
);
